import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ClientService } from '../../../services/client.service';
import { SiteService } from '../../../services/site.service';

import usePersistantState from 'hooks/state.hook';

import { Equipment, Client, RootState, Site, UUID, CommonUtilsService, CommonEquipmentService, Sector } from '@umanforge/common';
import { EquipmentFilter } from '@umanforge/common/dist/interfaces/filter.interfaces';
import { SectorService } from 'services/sector.service';

export function useEquipments() {

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  /*----------------------------- useState ------------------------------*/
  const [ clients, setClients ] = useState<( Client & { isCollapsed: boolean } )[]>( [] );
  const [ sites, setSites ] = useState< ( Site & { isCollapsed: boolean } )[]>( [] );
  const [ sectors, setSectors ] = useState<Sector[]>( [] );
  const [ equipments, setEquipments ] = useState<Equipment[]>( [] );

  const [ filterName, setFilterName ] = usePersistantState<string>( 'equipment_filterName', '' );
  const [ filterClients, setFilterClients ] = usePersistantState<UUID[]>( 'equipment_filterClients', [] );
  const [ filterSites, setFilterSites ] = usePersistantState<UUID[]>( 'equipment_filterSites', [] );
  const [ filterSectors, setFilterSectors ] = usePersistantState<UUID[]>( 'equipment_filterSectors', [] );

  const [ seeMoreClients, setSeeMoreClients ] = useState<boolean>( false );
  const [ isLoading, setIsLoading ] = useState<boolean>( true );
  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ isFilterEnd, setIsFilterEnd ] = useState<boolean>( false );

  const [ isOpenExportModal, setIsOpenExportModal ] = useState<boolean>( false );
  const [ isOpenImportModal, setIsOpenImportModal ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshFilters();
    }
  }, [ me ] );

  useEffect( () => {
    if ( isFilterEnd ) {
      checkClientFilter();
      checkSitesFilter();
      checkSectorsFilter();
      setTimeout( () => { setIsFirstRender( false ); }, 75 );
    }
  }, [ isFilterEnd ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshEquipments();
    }
  }, [ isFirstRender, filterName, filterClients, filterSites, filterSectors ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSeeMoreClients( filterClients.length > 0 || filterSites.length > 0 );
    }
  }, [ isFirstRender, filterClients, filterSites, filterSectors ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshFilters = async (): Promise<void> => {
    const responseClients = ( await ClientService.getAllowedClients( null, token, dispatch ) ?? [] )
      .map( client => {
        const currentClient = clients.find( item => { return item.id === client.id; } );
        const isCollapsed = ( !( me?.isSuperAdmin == true ) || ( currentClient != null ? currentClient.isCollapsed : false ) );
        return { ...client, isCollapsed };
      } );
    const responseSites = ( await SiteService.getAllowedSites( null, token, dispatch ) ?? [] )
      .map( site => {
        const currentSite = sites.find( item => { return item.id === site.id; } );
        const isCollapsed = ( !( me?.isSuperAdmin == true ) || ( currentSite != null ? currentSite.isCollapsed : false ) );
        return { ...site, isCollapsed };
      } );;
    const responseSectors = await SectorService.getAllowedSectors( null, token, dispatch ) ?? [];
    CommonUtilsService.sortByKey( responseClients, 'name', 'asc' );
    CommonUtilsService.sortByKey( responseSites, 'name', 'asc' );
    CommonUtilsService.sortByKey( responseSectors, 'name', 'asc' );
    setClients( responseClients );
    setSites( responseSites );
    setSectors( responseSectors );
    setIsFilterEnd( true );
  };

  const refreshEquipments = async (): Promise<void> => {
    setIsLoading( true );
    const filters: EquipmentFilter = {
      filterName: filterName && filterName != '' ? filterName : undefined,
      filterClients: filterClients && filterClients.length > 0 ? filterClients : undefined,
      filterSites: filterSites && filterSites.length > 0 ? filterSites : undefined,
      filterSectors: filterSectors && filterSectors.length > 0 ? filterSectors : undefined,
    };
    const responseEquipments = await CommonEquipmentService.getAllowEquipments( filters, token, dispatch );

    responseEquipments.sort( ( equipment1, equipment2 ) => {
      const sector1 = sectors.find( sector => { return sector.id === equipment1.sectorId; } );
      const sector2 = sectors.find( sector => { return sector.id === equipment2.sectorId; } );
      const site1 = sites.find( site => { return site.id === sector1?.siteId; } );
      const site2 = sites.find( site => { return site.id === sector2?.siteId; } );
      const client1 = clients.find( client => { return client.id === site1?.clientId; } );
      const client2 = clients.find( client => { return client.id === site2?.clientId; } );

      if ( client1 && client2 ) {
        if ( client1?.name < client2.name ) { return -1; }
        if ( client1?.name > client2.name ) { return 1; }
      }
      if ( site1 && site2 ) {
        if ( site1.name < site2.name ) { return -1; }
        if ( site1.name > site2.name ) { return 1; }
      }
      if ( sector1 && sector2 ) {
        if ( sector1.name < sector2.name ) { return -1; }
        if ( sector1.name > sector2.name ) { return 1; }
      }
      if ( equipment1 && equipment2 && equipment1.name < equipment2.name ) { return -1; }
      if ( equipment1 && equipment2 && equipment1.name > equipment2.name ) { return 1; }
      return 0;
    } );

    setEquipments( responseEquipments );
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const checkClientFilter = (): void => {
    const newFilter = filterClients
      .filter( clientIdFilter => {
        return clients.find( client => { return client.id === clientIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterClients.length ) {
      setFilterClients( newFilter );
    }
  };

  const checkSitesFilter = (): void => {
    const sitesOfClients = sites.filter( site => { return clients.find( client => { return client.id === site.clientId; } ) != null; } );
    const newFilter = filterSites
      .filter( siteIdFilter => {
        return sitesOfClients.find( site => { return site.id === siteIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterSites.length ) {
      setFilterSites( newFilter );
    }
  };

  const checkSectorsFilter = (): void => {
    const sectorsOfClients = sectors.filter( sector => { return sites.find( site => { return site.id === sector.siteId; } ) != null; } );
    const newFilter = filterSectors
      .filter( sectorIdFilter => {
        return sectorsOfClients.find( sector => { return sector.id === sectorIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterSectors.length ) {
      setFilterSectors( newFilter );
    }
  };

  /*------------------------------ Events -------------------------------*/
  const onChangeFilteredClients = ( clientId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterClients( [ ...filterClients, clientId ] );
    } else {
      const copyFilterClients = [ ...filterClients ];
      const index = copyFilterClients.findIndex( item => { return item === clientId; } );
      if ( index != -1 ) {
        copyFilterClients.splice( index, 1 );
      }
      setFilterClients( copyFilterClients );
    }
  };

  const onChangeFilteredSites = ( siteId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterSites( [ ...filterSites, siteId ] );
    } else {
      const copyFilterSites = [ ...filterSites ];
      const index = copyFilterSites.findIndex( item => { return item === siteId; } );
      if ( index != -1 ) {
        copyFilterSites.splice( index, 1 );
      }
      setFilterSites( copyFilterSites );
    }
  };

  const onChangeFilteredSectors = ( sectorId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterSectors( [ ...filterSectors, sectorId ] );
    } else {
      const copyFilterSectors = [ ...filterSectors ];
      const index = copyFilterSectors.findIndex( sector => { return sector === sectorId; } );
      if ( index != -1 ) {
        copyFilterSectors.splice( index, 1 );
      }
      setFilterSectors( copyFilterSectors );
    }
  };

  const openExportModal = () => {
    setIsOpenExportModal( true );
  };
  const closeExportModal = () => {
    setIsOpenExportModal( false );
  };

  const openImportModal = () => {
    setIsOpenImportModal( true );
  };
  const closeImportModal = ( isImported?: boolean ) => {
    setIsOpenImportModal( false );
    if ( isImported === true ) {
      refreshEquipments();
    }
  };

  return {
    clients, setClients,
    sites, setSites,
    sectors, setSectors,
    equipments, setEquipments,
    filterName, setFilterName,
    filterClients, setFilterClients,
    filterSites, setFilterSites,
    filterSectors, setFilterSectors,
    seeMoreClients, setSeeMoreClients,
    isLoading, setIsLoading,
    isFirstRender, setIsFirstRender,
    isFilterEnd, setIsFilterEnd,
    onChangeFilteredSectors,
    onChangeFilteredSites,
    onChangeFilteredClients,
    isOpenExportModal, openExportModal, closeExportModal,
    isOpenImportModal, openImportModal, closeImportModal
  };
}